/**
 * Compresses an image file before uploading to Supabase Storage or saving to database.
 * Resizes to max 1280x720 (standard 16:9 thumbnail) and compresses to WebP/JPEG ~80KB.
 */
export async function compressImage(
  file: File,
  maxWidth = 1280,
  maxHeight = 720,
  quality = 0.85,
  preferredMimeType = "image/webp"
): Promise<{ file: File; dataUrl: string }> {
  return new Promise((resolve) => {
    // If browser doesn't support Image/Canvas or if not an image, resolve raw file
    if (typeof window === "undefined" || !file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve({ file, dataUrl: (reader.result as string) || "" });
      };
      reader.readAsDataURL(file);
      return;
    }

    const img = document.createElement("img");
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions maintaining aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve({ file, dataUrl: img.src });
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Prefer WebP for optimal compression, fallback to JPEG
      let mimeType = preferredMimeType;
      let dataUrl = "";
      try {
        dataUrl = canvas.toDataURL(mimeType, quality);
        if (!dataUrl.startsWith(`data:${mimeType}`)) {
          mimeType = "image/jpeg";
          dataUrl = canvas.toDataURL(mimeType, quality);
        }
      } catch {
        mimeType = "image/jpeg";
        dataUrl = canvas.toDataURL(mimeType, quality);
      }

      const ext = mimeType.includes("webp") ? ".webp" : ".jpg";

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve({ file, dataUrl });
            return;
          }
          const compressedFile = new File(
            [blob],
            file.name.replace(/\.[^.]+$/, "") + ext,
            {
              type: mimeType,
              lastModified: Date.now(),
            }
          );
          resolve({ file: compressedFile, dataUrl });
        },
        mimeType,
        quality
      );
    };

    img.onerror = () => {
      const fallbackReader = new FileReader();
      fallbackReader.onloadend = () => {
        resolve({ file, dataUrl: (fallbackReader.result as string) || "" });
      };
      fallbackReader.readAsDataURL(file);
    };

    reader.readAsDataURL(file);
  });
}
